import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../AppContext';
import {
  GetAllScheduledDocument,
  IGetAllScheduledQuery,
  IScheduler,
  useDeleteSchedulerMutation,
  useGetAllScheduledQuery,
  useGetSendGridDataForExcelLazyQuery,
  useDeleteRecipientListFromSchedulerMutation
} from '../../generated/graphql';
import { Settings, AssignmentReturned, FileCopy, Schedule } from '@mui/icons-material';
import moment from 'moment';
import { Actions } from '../../enums/ActionEnums';
import { Types } from '../../enums/TypeEnums';
import { exportDataAsExcel } from '../../utils/fileUtiles';
import { canUserCreate } from '../../utils/rightUtils';
import { Colors } from '../../utils/theme';
import { Box, Button, Chip, Grid, SxProps } from '@mui/material';
import OverviewTable, { IHeadCell } from '../Common/Tables/OverviewTable';
import EpraDatePicker from '../Common/Fields/EpraDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import offline from '../../assets/images/actions/offline.svg';
import online from '../../assets/images/actions/online.svg';
import Loader from '../Common/Loader';
import SchedulerForm from './SchedulerForm';
import SchedulerDetails from './SchedulerDetails';

type RemoveRecipientListInput = {
  scheduler_id: string;
  recipient_list_id: string;
};

const deleteIconStyles: SxProps = {
  color: Colors.skyBlue,
  width: 28,
  height: 28,
  '&:hover': {
    color: Colors.main
  }
};

const chipStyles: SxProps = {
  color: Colors.main,
  fontSize: 14,
  fontWeight: 'normal',
  background: '#69AAF333',
  '&:active, &:focus': {
    background: '#69AAF333'
  }
};

const Scheduler: React.FunctionComponent = () => {
  const minAllowedDate = new Date(2000, 0, 1);
  const { dispatch, state } = useContext(AppContext);
  const { data, loading } = useGetAllScheduledQuery();
  const [deleteRecipientListFromScheduler] = useDeleteRecipientListFromSchedulerMutation();
  const [allScheduled, setAllScheduled] = useState<IScheduler[]>([]);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [filteredScheduled, setFilteredScheduled] = useState<IScheduler[]>([]);
  const [openSchedulerForm, setOpenSchedulerForm] = useState(false);
  const [openActivityLogForm, setOpenActivityLogForm] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [deleteScheduler] = useDeleteSchedulerMutation();
  const [selectedScheduler, setSelectedScheduler] = useState<IScheduler | null>(null);
  const [getMailActivityLog, mailActivityLogResult] = useGetSendGridDataForExcelLazyQuery({
    fetchPolicy: 'no-cache'
  });
  const canCreate = canUserCreate(state.user, Types.Scheduler);

  useEffect(() => {
    if (
      mailActivityLogResult.data &&
      mailActivityLogResult.data.result &&
      mailActivityLogResult.data.result.length
    ) {
      const fileName = `${moment(new Date()).format(
        'YYYY_MM_DD'
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
      )}_${mailActivityLogResult.data.result[0]?.scheduler.name.replaceAll(' ', '_')}`;
      const csvData = mailActivityLogResult.data.result.map((r) => ({
        Email: r?.email,
        Status: r?.status,
        'Opens Count': r?.opens_count,
        'Clicks Count': r?.clicks_count
      }));
      exportDataAsExcel(csvData, fileName);
    }
  }, [mailActivityLogResult]);

  useEffect(() => {
    let filteredByDate = allScheduled;
    const currentStartDate = startDate ? new Date(startDate) : null;
    const currentEndDate = endDate ? new Date(endDate) : null;
    const cleanStartDate = currentStartDate
      ? new Date(
          currentStartDate.getFullYear(),
          currentStartDate.getMonth(),
          currentStartDate.getDate(),
          0o0,
          0o0,
          0o0
        ).getTime()
      : null;
    const cleanEndDate = currentEndDate
      ? new Date(
          currentEndDate.getFullYear(),
          currentEndDate.getMonth(),
          currentEndDate.getDate() + 1,
          0o0,
          0o0,
          0o0
        ).getTime()
      : null;

    if (currentStartDate && currentEndDate) {
      if (currentStartDate > currentEndDate) {
        filteredByDate = allScheduled.filter(
          (allS) => new Date(parseInt(allS.scheduled_date)).getTime() >= (cleanStartDate as number)
        );
      } else {
        filteredByDate = allScheduled.filter((allS) => {
          const scheduledDate = new Date(parseInt(allS.scheduled_date)).getTime();
          return (
            scheduledDate >= (cleanStartDate as number) && scheduledDate < (cleanEndDate as number)
          );
        });
      }
    } else if (currentEndDate && !currentStartDate) {
      filteredByDate = allScheduled.filter(
        (allS) => new Date(parseInt(allS.scheduled_date)).getTime() < (cleanEndDate as number)
      );
    } else if (currentStartDate && !currentEndDate) {
      filteredByDate = allScheduled.filter(
        (allS) => new Date(parseInt(allS.scheduled_date)).getTime() >= (cleanStartDate as number)
      );
    }
    setFilteredScheduled(filteredByDate);
  }, [startDate, endDate]);

  useEffect(() => {
    if (data && data.result) {
      setAllScheduled(data.result as IScheduler[]);
      setFilteredScheduled(data.result as IScheduler[]);
    }
  }, [data]);

  const handleDelete = (rowData: IScheduler) => {
    if (!rowData.id) {
      return;
    }
    dispatch({ type: Actions.HideConfirmation });
    deleteScheduler({
      variables: {
        id: parseInt(rowData.id)
      },
      update(cache) {
        const schedulerQuery = cache.readQuery({
          query: GetAllScheduledDocument
        }) as IGetAllScheduledQuery;
        if (schedulerQuery.result) {
          const cacheSchedulerIndex = schedulerQuery.result.findIndex((s) => s?.id === rowData.id);
          if (cacheSchedulerIndex > -1) {
            const data = schedulerQuery.result.filter((s) => s?.id !== rowData.id);
            cache.writeQuery({
              query: GetAllScheduledDocument,
              data: { result: data }
            });
          }
        }
        dispatch({
          type: Actions.ShowMessage,
          payload: { text: `Scheduler deleted ${rowData.name}`, severity: 'success' }
        });
      }
    }).catch((error) =>
      dispatch({
        type: Actions.ShowMessage,
        payload: {
          severity: 'error',
          text: error.message,
          autoHide: 6000
        }
      })
    );
  };

  const handleRemove = (rowData: IScheduler) => {
    dispatch({
      type: Actions.ShowConfirmation,
      payload: {
        text: `Are you sure you want to delete ${rowData.name}?`,
        agreeAction: handleDelete,
        params: rowData
      }
    });
  };

  const handleDetails = (rowData: IScheduler) => {
    setSelectedScheduler(rowData);
    setOpenActivityLogForm(true);
  };

  const handleCloseActivityLogForm = () => {
    setSelectedScheduler(null);
    setOpenActivityLogForm(false);
    setIsEditMode(false);
  };

  const handleEdit = (rowData: IScheduler) => {
    setSelectedScheduler(rowData);
    setIsEditMode(true);
    setOpenSchedulerForm(true);
  };

  const handleDuplicate = (rowData: IScheduler) => {
    setSelectedScheduler(rowData);
    setIsEditMode(false);
    setOpenSchedulerForm(true);
  };

  const handleCloseForm = () => {
    setSelectedScheduler(null);
    setIsEditMode(false);
    setOpenSchedulerForm(false);
  };

  const handleExport = (rowData: IScheduler) => {
    getMailActivityLog({ variables: { scheduler_id: parseInt(rowData.id) } });
  };

  const isEmailSent = (rowData: IScheduler) =>
    rowData.is_success === null ? (
      <Schedule />
    ) : (
      <img
        className="m-auto"
        src={rowData.is_success ? online : rowData.is_success === null ? '' : offline}
      />
    );

  const formatDate = (rowData: IScheduler, name: string) => {
    const raw = rowData[name as keyof IScheduler];
    if (raw == null || raw === '') {
      return '';
    }
    const value = String(raw);
    // Supports epoch ms strings from API and ISO dates
    const parsed = /^\d+$/.test(value) ? moment(parseInt(value, 10)) : moment(value);
    return parsed.isValid() ? parsed.format('MM/DD/YYYY HH:mm') : '';
  };

  const renderSettingsColumn = (rowData: IScheduler) => {
    return (
      <Button id={`settings-${rowData.id}`} onClick={() => handleEdit(rowData)}>
        <Settings style={{ color: Colors.skyBlue }} />
      </Button>
    );
  };

  const renderExportColumn = (rowData: IScheduler) => {
    return (
      <Button id={`export-${rowData.id}`} onClick={() => handleExport(rowData)}>
        <AssignmentReturned style={{ color: Colors.skyBlue }} />
      </Button>
    );
  };

  const renderDuplicateColumn = (rowData: IScheduler) => {
    const scheduler = { ...rowData, name: '' };
    return (
      <Button id={`duplicate-${rowData.id}`} onClick={() => handleDuplicate(scheduler)}>
        <FileCopy style={{ color: Colors.skyBlue }} />
      </Button>
    );
  };

  const renderRecipientLists = (rowData: IScheduler) => {
    return (
      <Grid container>
        {rowData.recipient_lists?.map((rl) => (
          <Grid
            item
            xs={'auto'}
            key={`${rowData.id}-${rl?.id}-recipient_list`}
            style={{ margin: 5 }}
          >
            <Chip
              id={`${rowData.id}-${rl?.id}-chip`}
              label={rl?.name}
              onDelete={() => {
                dispatch({
                  type: Actions.ShowConfirmation,
                  payload: {
                    text: `Are you sure you want to remove ${rl.name} from ${rowData.name} scheduler`,
                    agreeAction: handleRemoveRecipientListFromScheduler,
                    params: {
                      scheduler_id: rowData.id,
                      recipient_list_id: rl.id
                    }
                  }
                });
              }}
              sx={{
                ...chipStyles,
                '.MuiChip-deleteIcon': deleteIconStyles
              }}
            />
          </Grid>
        ))}
      </Grid>
    );
  };

  const handleRemoveRecipientListFromScheduler = (input: RemoveRecipientListInput) => {
    dispatch({
      type: Actions.HideConfirmation
    });
    deleteRecipientListFromScheduler({
      variables: {
        scheduler_id: input.scheduler_id,
        recipient_list_id: input.recipient_list_id
      },
      update(cache, { data }) {
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            text: `Recipient List removed from scheduler`,
            severity: 'success'
          }
        });
        if (data && data.result) {
          const updatedScheduler = data.result as IScheduler;
          const schedulersQuery = cache.readQuery({
            query: GetAllScheduledDocument
          }) as IGetAllScheduledQuery;
          if (schedulersQuery.result) {
            const cachedSchedulers = schedulersQuery.result.findIndex(
              (s) => s?.id === updatedScheduler.id
            );
            if (cachedSchedulers > -1) {
              const updatedData = schedulersQuery.result.map((s) =>
                s?.id == updatedScheduler.id ? updatedScheduler : s
              );
              cache.writeQuery({
                query: GetAllScheduledDocument,
                data: { result: updatedData }
              });
            }
          }
        }
      }
    }).catch((e) => {
      dispatch({
        type: Actions.ShowMessage,
        payload: { text: e.message, severity: 'error', autoHide: 6000 }
      });
    });
  };

  const columns = [
    {
      title: 'Details',
      width: 62.5,
      sorting: false
    },
    {
      title: 'Configure',
      width: 80,
      sorting: false,
      render: renderSettingsColumn
    },
    {
      title: 'Duplicate',
      width: 93,
      sorting: false,
      render: renderDuplicateColumn
    },
    {
      title: 'Name',
      width: 290,
      field: 'name'
    },
    {
      title: 'Sender',
      width: 300,
      field: 'sender.email'
    },
    {
      title: 'Email Status',
      width: 130,
      field: 'is_success',
      sorting: false,
      render: isEmailSent,
      type: 'boolean'
    },
    {
      title: 'Template',
      width: 295,
      field: 'template.title'
    },
    {
      title: 'Recipient Lists',
      width: 246,
      render: (rowData: IScheduler) => renderRecipientLists(rowData)
    },
    {
      title: 'Scheduled Date',
      width: 250,
      field: 'scheduled_date',
      type: 'date',
      render: (rowData: IScheduler) => formatDate(rowData, 'scheduled_date')
    },
    {
      title: 'Export',
      width: 85,
      render: renderExportColumn,
      sorting: false
    },
    {
      title: 'Remove',
      width: 71.5,
      sorting: false
    }
  ] as IHeadCell[];

  return (
    <Box className="overviewPage">
      <Box>
        <OverviewTable
          columns={columns}
          data={filteredScheduled || []}
          loading={loading}
          handleRemove={handleRemove}
          handleDetails={handleDetails}
          pageSize={10}
          createButtonProps={{
            id: 'create-scheduler-button',
            onClick: () => setOpenSchedulerForm(true),
            text: 'Create Scheduler',
            disabled: !canCreate
          }}
          additionalFilters={
            <Grid
              container
              style={{
                maxWidth: '350px'
              }}
            >
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Grid item xs={6}>
                  <EpraDatePicker
                    id="start-date-picker"
                    label="Start Date"
                    value={startDate}
                    onChange={(date) => setStartDate(date)}
                    sx={{
                      maxWidth: '175px',
                      marginRight: '10px'
                    }}
                    minDate={minAllowedDate}
                  />
                </Grid>
                <Grid xs={6}>
                  <EpraDatePicker
                    id="end-date-picker"
                    label="End Date"
                    value={endDate}
                    onChange={(date) => setEndDate(date)}
                    minDate={startDate || minAllowedDate}
                    sx={{
                      maxWidth: '175px',
                      marginLeft: '10px'
                    }}
                  />
                </Grid>
              </LocalizationProvider>
            </Grid>
          }
        />
        {openSchedulerForm && (
          <SchedulerForm
            handleClose={handleCloseForm}
            scheduler={selectedScheduler}
            isEditMode={isEditMode}
          />
        )}
        {openActivityLogForm && (
          <SchedulerDetails
            scheduler={selectedScheduler as IScheduler}
            handleClose={handleCloseActivityLogForm}
          />
        )}
        <Loader loading={mailActivityLogResult.loading} />
      </Box>
    </Box>
  );
};

export default Scheduler;
